import React from 'react';
import {LoadingSpinner} from 'components';
import {ProviderHeader} from 'components/Headers/ProviderHeader';
import {RevenueChart} from 'features/Provider/Revenue/components/RevenueChart';
import {useRevenueSessions} from 'hooks';
import {Session} from 'interfaces';
import {Helmet} from 'react-helmet-async';
import {useTranslation} from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import {useLocation} from 'react-router';

import {FilterSection} from './components/FilterSection';
import {SessionLoaders} from './components/SessionLoaders';

export interface RevenueChildProps {
  memoizedSessions: Session[];
}

export function RevenueSession({
  children,
}: {
  children?: React.ReactElement<RevenueChildProps>;
}) {
  const location = useLocation();
  const isSingleSession = location.pathname.includes('single-sessions');
  const {t} = useTranslation();

  const {
    sessions,
    hasNextPage,
    hasNoSessions,
    billingSessionsStatus,
    handleFilterChange,
    loadMoreData,
  } = useRevenueSessions({isSingleSession});

  return (
    <>
      <Helmet>
        <title>{t('title.revenue')}</title>
      </Helmet>
      <ProviderHeader title={t('revenue')} showBookButtonInMobile={true} />
      <div className="p-8 flex flex-col">
        <div className="mb-8">
          <h1 className="text-2xl font-semibold">Overview</h1>
          <p className="text-gray-600 text-base mt-2">
            This is your current itemized history of sessions. Billable sessions
            will be automatically added to your Deel account by the end of the
            cycle.
          </p>
        </div>
        <RevenueChart />
        <h1 className="text-2xl font-bold">
          {isSingleSession ? 'Single Session' : 'Group Session'}
        </h1>
        <FilterSection onFilterChange={handleFilterChange} />
        <div className="mt-8">
          {hasNoSessions ? (
            <div className="text-center py-8">
              <p className="text-gray-600 text-lg">
                {t(
                  'revenue.noSessionsFound',
                  'No sessions found matching your filters.',
                )}
              </p>
            </div>
          ) : (
            <InfiniteScroll
              dataLength={sessions.length}
              hasMore={hasNextPage}
              next={loadMoreData}
              scrollableTarget="dashboardScroll"
              endMessage={
                <SessionLoaders
                  billingSessionsStatus={billingSessionsStatus}
                  hasNextPage={hasNextPage}
                />
              }
              loader={
                <div className="w-full flex items-center justify-center my-4">
                  <LoadingSpinner height={50} width={50} type="Oval" />
                </div>
              }
            >
              {children}
            </InfiniteScroll>
          )}
        </div>
      </div>
    </>
  );
}
